import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  FormBase,
  type IApiThrowsError,
  type IBaseModalProps,
  INIT_PAGE,
  ListLoading,
  Logger,
  NO_LIMIT_PAGE,
  TemplatesType,
} from '@gbs-monorepo-packages/common';

import { type IContributionDataDTO } from '../../../../services/contributionClass';
import { type IPlanDTO } from '../../../../services/plan';
import {
  type AutobuildSpreadPlansOrder,
  type IAutobuildPageDTO,
  type IGenerateAutobuildPage,
  type IPageType,
  IQuoteDateDTO,
  getAutobuildPages,
} from '../../../../services/quote';
import {
  ITemplateGroupDTO,
  getTemplateGroupsByClientId,
} from '../../../../services/templates';
import { CustomSelectData } from '../CustomSelectData';
import { ButtonArrows } from './components/ButtonArrows';
import {
  CheckContainer,
  Container,
  CustomAccordion,
  CustomAccordionContent,
  CustomAccordionItem,
  CustomAccordionTrigger,
  CustomBaseCheckbox,
  CustomBaseTooltip,
  CustomTable,
  DividerTitle,
  ErrorMessage,
  FormModalWide,
  InfoIcon,
  LabelCheckBox,
  LabelCheckContainer,
  ListContainer,
  PlanGroupName,
  SpacedContainer,
  TableContainer,
  Tbody,
  Td,
  TdArrows,
  Text,
  TextContainer,
  TextTitle,
  Th,
  Tr,
  TrSpreadPlan,
} from './styles';

interface ITemplateGroupSelectData {
  key: number;
  value: string;
}

interface IError {
  error?: string;
}

type GroupedPlans = Record<string, IPlanDTO[]>;
type GroupedContribution = IContributionDataDTO[];
type ClickPageTypeOrders = Record<number, Record<string, number>>;
type ClickPageContributionTypeOrders = Record<number, number>;
type PageTypePlanOrder = Record<number, Record<string, Record<number, number>>>;
type PageTypeContributionOrder = Record<number, Record<number, number>>;

interface IPagePlanData {
  pageId: number;
  data: Array<{
    type: string;
    plans: number[];
    qtd: number;
  }>;
}

interface IPageContributionData {
  pageId: number;
  data: number[];
}

interface IGroupedPages extends IAutobuildPageDTO {
  groupedPlans: GroupedPlans;
  groupedContributions: GroupedContribution;
}

interface IAutoBuildModalProps
  extends Partial<Omit<IBaseModalProps, 'children' | 'onOpenChange'>> {
  onAccept: (plans: any) => void;
  onDecline: () => void;
  open: boolean;
  isActionRunning: boolean;
  quotesDatesSelected: IQuoteDateDTO;
}

interface ITemplateGroupSelectData {
  key: number;
  value: string;
}

export const AutoBuildModal = ({
  onAccept,
  onDecline,
  open,
  zIndex,
  isActionRunning,
  quotesDatesSelected,
  ...props
}: IAutoBuildModalProps): JSX.Element | null => {
  const isSelectOpen = useRef(new Set());
  const [templateGroupId, setTemplateGroupId] = useState<number>();
  const [error, setError] = useState<IError>({});
  const [loadingGePagePlans, setLoadingGePagePlans] = useState<boolean>(false);

  const [templatesGroups, setTemplatesGroups] = useState<
    ITemplateGroupSelectData[]
  >([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [groupedPagesPlans, setGroupedPagesPlans] = useState<IGroupedPages[]>(
    []
  );
  const [selectedPagePlans, setSelectedPagePlans] = useState<IPagePlanData[]>(
    []
  );
  const [selectedPageContributions, setSelectedPageContributions] = useState<
    IPageContributionData[]
  >([]);
  const [pagePlanOrderType, setPagePlanOrderType] = useState<PageTypePlanOrder>(
    {}
  );
  const [pageContributionOrderType, setPageContributionOrderType] =
    useState<PageTypeContributionOrder>({});

  const { clientId = '' } = useParams();
  const loadedOnce = useRef(false);
  const isLoading = loadingGePagePlans || loadingTemplates || isActionRunning;

  const replaceTypeName = (type: string): string => {
    if (type.toLowerCase().includes('medical')) return 'medical';
    if (type.toLowerCase().includes('dental')) return 'dental';
    if (type.toLowerCase().includes('vision')) return 'vision';
    if (type.toLowerCase().includes('hospital')) return 'hospital';
    if (type.toLowerCase().includes('accident')) return 'accident';
    if (type.toLowerCase().includes('critical')) return 'criticalIllness';
    if (type.toLowerCase().includes('contribution')) return 'contribution';

    return type.toLowerCase();
  };

  const handleOpenChange = useCallback((isOpen: boolean, key: string) => {
    if (isOpen) {
      isSelectOpen.current.add(key);
    } else {
      isSelectOpen.current.delete(key);
    }
  }, []);

  useEffect(() => {
    let mount = true;

    const loadTemplatesOnce = async () => {
      loadedOnce.current = true;
      setLoadingTemplates(true);
      try {
        const result = await getTemplateGroupsByClientId({
          filter: '',
          limit: NO_LIMIT_PAGE,
          page: INIT_PAGE,
          templateOptionsId: TemplatesType.AUTOBUILD_TEMPLATES,
        });
        if (mount) {
          const templatesGroup = result.data.map((group: ITemplateGroupDTO) => {
            return {
              key: group.id,
              value: group.title,
            };
          });

          setTemplatesGroups(templatesGroup);
        }
      } catch (err) {
        loadedOnce.current = false;
      } finally {
        setTimeout(() => {
          if (mount) {
            setLoadingTemplates(false);
          }
        }, 200);
      }
    };

    if (clientId && !loadedOnce.current) {
      void loadTemplatesOnce();
    }

    return () => {
      mount = false;
      loadedOnce.current = false;
    };
  }, [clientId]);

  const groupPlansByPage = useCallback(
    (pages: IAutobuildPageDTO[]): IGroupedPages[] => {
      const result: IGroupedPages[] = pages.map((page: IAutobuildPageDTO) => {
        const groupedPlans: GroupedPlans = {};
        const groupedContributions: GroupedContribution = [];

        page.spreadPlans.forEach((plan) => {
          const type = replaceTypeName(plan.type);
          if (!groupedPlans[type]) {
            groupedPlans[type] = [];
          }
          groupedPlans[type].push(plan);
        });

        page.contributionData.forEach((contribution) => {
          groupedContributions.push(contribution);
        });

        return {
          pageType: page.pageType,
          pageId: page.pageId,
          title: page.title,
          spreadPlans: page.spreadPlans,
          contributionData: page.contributionData,
          groupedPlans,
          groupedContributions,
        };
      });

      return result;
    },
    []
  );

  const getPlansByPage = useCallback(
    (templateCourseId: number) => {
      if (!quotesDatesSelected) return;
      setLoadingGePagePlans(true);
      getAutobuildPages(quotesDatesSelected.id, templateCourseId)
        .then((response: IAutobuildPageDTO[]) => {
          setGroupedPagesPlans(groupPlansByPage(response));
        })
        .catch((error: IApiThrowsError) => {
          Logger.debug('error: ', error);
        })
        .finally(() => {
          setLoadingGePagePlans(false);
        });
    },
    [quotesDatesSelected, groupPlansByPage]
  );

  const togglePagePlanSelection = (
    planId: number,
    pageId: number,
    planType: string
  ) => {
    setSelectedPagePlans((prevSelectedPlans) => {
      const pageIndex = prevSelectedPlans.findIndex(
        (pagePlan) => pagePlan.pageId === pageId
      );

      const planTypeAux = replaceTypeName(planType);
      if (pageIndex !== -1) {
        const pageData = prevSelectedPlans[pageIndex].data;
        const typeIndex = pageData.findIndex(
          (type) =>
            type.type.toLocaleLowerCase() === planTypeAux.toLocaleLowerCase()
        );

        if (typeIndex !== -1) {
          const planIndex = pageData[typeIndex].plans.indexOf(planId);
          if (planIndex !== -1) {
            pageData[typeIndex].plans.splice(planIndex, 1);
          } else {
            pageData[typeIndex].plans.push(planId);
          }

          if (pageData[typeIndex].plans.length === 0) {
            pageData.splice(typeIndex, 1);
          }

          if (pageData[typeIndex] !== undefined) {
            pageData[typeIndex].qtd =
              pageData[typeIndex] !== undefined
                ? pageData[typeIndex].plans.length
                : 0;
          }
        } else {
          pageData.push({
            type: planTypeAux,
            plans: [planId],
            qtd: 1,
          });
        }

        if (pageData.length === 0) {
          prevSelectedPlans.splice(pageIndex, 1);
        }
      } else {
        prevSelectedPlans.push({
          pageId,
          data: [
            {
              type: planTypeAux,
              plans: [planId],
              qtd: 1,
            },
          ],
        });
      }

      return [...prevSelectedPlans];
    });
  };

  const togglePageContributionSelection = (
    contributionId: number,
    pageId: number
  ) => {
    setSelectedPageContributions((prevSelectedPageContributions) => {
      const pageIndex = prevSelectedPageContributions.findIndex(
        (pagePlan) => pagePlan.pageId === pageId
      );

      if (pageIndex !== -1) {
        const pageData = prevSelectedPageContributions[pageIndex].data;

        const planIndex = pageData.indexOf(contributionId);
        if (planIndex !== -1) {
          pageData.splice(planIndex, 1);
        } else {
          pageData.push(contributionId);
        }

        if (pageData.length === 0) {
          prevSelectedPageContributions.splice(pageIndex, 1);
        }
      } else {
        prevSelectedPageContributions.push({
          pageId,
          data: [contributionId],
        });
      }

      return [...prevSelectedPageContributions];
    });
  };

  const handleArrowClick = useCallback(
    (
      direction: 'down' | 'up',
      planId: number,
      prop: string,
      type = 'plan',
      pageId: number
    ) => {
      if (type === 'plan') {
        const pagePlanOrderTypeAux: PageTypePlanOrder = {};

        setGroupedPagesPlans((prevGroupedPages) => {
          const updatedGroupedPages = [...prevGroupedPages];
          const pageIndex = updatedGroupedPages.findIndex(
            (page) => page.pageId === pageId
          );

          if (
            pageIndex === -1 ||
            !updatedGroupedPages[pageIndex]?.groupedPlans?.[prop]
          ) {
            return updatedGroupedPages;
          }

          const plans = updatedGroupedPages[pageIndex].groupedPlans[prop];
          const filteredOrderedPlans = plans.filter(
            (plan) => plan.order !== undefined
          );

          const index = filteredOrderedPlans.findIndex(
            (plan) => plan.id === planId
          );
          if (index === -1) {
            return updatedGroupedPages;
          }

          const newPlans = [...plans];
          const swapPlans = swapOrder(filteredOrderedPlans, index, direction);

          swapPlans.forEach((swappedPlan) => {
            const originalIndex = plans.findIndex(
              (plan) => plan.id === swappedPlan.id
            );
            if (originalIndex !== -1) {
              newPlans[originalIndex] = swappedPlan;
              if (swappedPlan.order) {
                if (!pagePlanOrderTypeAux[pageId]) {
                  pagePlanOrderTypeAux[pageId] = {};
                }

                if (!pagePlanOrderTypeAux[pageId][prop]) {
                  pagePlanOrderTypeAux[pageId][prop] = {};
                }

                pagePlanOrderTypeAux[pageId][prop][swappedPlan.id] =
                  swappedPlan.order;
              }
            }
          });

          updatedGroupedPages[pageIndex] = {
            ...updatedGroupedPages[pageIndex],
            groupedPlans: {
              ...updatedGroupedPages[pageIndex].groupedPlans,
              [prop]: newPlans,
            },
          };

          return updatedGroupedPages;
        });

        setPagePlanOrderType((prevPagePlanOrderType) => {
          const updatedPagePlanOrderType = {
            ...prevPagePlanOrderType,
            ...pagePlanOrderTypeAux,
          };

          return updatedPagePlanOrderType;
        });
      } else {
        const pageContributionOrderTypeAux: PageTypeContributionOrder = {};

        setGroupedPagesPlans((prevGroupedPages) => {
          const updatedGroupedPages = [...prevGroupedPages];
          const pageIndex = updatedGroupedPages.findIndex(
            (page) => page.pageId === pageId
          );

          if (
            pageIndex === -1 ||
            !updatedGroupedPages[pageIndex]?.groupedContributions
          ) {
            return updatedGroupedPages;
          }

          const contributions =
            updatedGroupedPages[pageIndex].groupedContributions;
          const filteredOrderedContribution = contributions.filter(
            (plan) => plan.order !== undefined
          );

          const index = filteredOrderedContribution.findIndex(
            (contribution) => contribution.id === planId
          );
          if (index === -1) {
            return updatedGroupedPages;
          }

          const newContributions = [...contributions];
          const swapContributions = swapOrder(
            filteredOrderedContribution,
            index,
            direction
          );

          swapContributions.forEach((swappedContribution) => {
            const originalIndex = contributions.findIndex(
              (contribution) => contribution.id === swappedContribution.id
            );
            if (originalIndex !== -1) {
              newContributions[originalIndex] = swappedContribution;
              if (swappedContribution.order) {
                if (!pageContributionOrderTypeAux[pageId]) {
                  pageContributionOrderTypeAux[pageId] = {};
                }

                if (!pageContributionOrderTypeAux[pageId]) {
                  pageContributionOrderTypeAux[pageId] = {};
                }

                pageContributionOrderTypeAux[pageId][swappedContribution.id] =
                  swappedContribution.order;
              }
            }
          });

          updatedGroupedPages[pageIndex] = {
            ...updatedGroupedPages[pageIndex],
            groupedContributions: newContributions,
          };

          return updatedGroupedPages;
        });

        setPageContributionOrderType((prevPageContributionOrderType) => {
          const updatedPagePlanOrderType = {
            ...prevPageContributionOrderType,
            ...pageContributionOrderTypeAux,
          };

          return updatedPagePlanOrderType;
        });
      }
    },
    []
  );

  const swapOrder = <T extends { order?: number }>(
    items: T[],
    selectedIndex: number,
    direction: 'down' | 'up'
  ): T[] => {
    const lastIndex = items.length - 1;

    if (
      (direction === 'up' && selectedIndex === 0) ||
      (direction === 'down' && selectedIndex === lastIndex)
    ) {
      return items;
    }

    const newItems = [...items];
    const selectedItem = newItems[selectedIndex];
    const adjacentIndex =
      direction === 'up' ? selectedIndex - 1 : selectedIndex + 1;
    const adjacentItem = newItems[adjacentIndex];

    newItems[selectedIndex] = { ...selectedItem, order: adjacentItem.order };
    newItems[adjacentIndex] = { ...adjacentItem, order: selectedItem.order };

    return newItems;
  };

  useEffect(() => {
    const pagePlanOrderTypeAux: PageTypePlanOrder = {};
    const clickOrdersAux: ClickPageTypeOrders = {};
    const updatedGroupedPagesPlans = [...groupedPagesPlans];

    selectedPagePlans.forEach((pagePlan) => {
      const pageNumber = pagePlan.pageId;
      const page = pagePlan.data;
      if (!pagePlanOrderTypeAux[pageNumber]) {
        pagePlanOrderTypeAux[pageNumber] = {};
        clickOrdersAux[pageNumber] = {};
      }

      for (const type of page) {
        const plans = type.plans;
        const typeLowerCase = replaceTypeName(type.type);

        if (!pagePlanOrderTypeAux[pageNumber][typeLowerCase]) {
          pagePlanOrderTypeAux[pageNumber][typeLowerCase] = {};
          clickOrdersAux[pageNumber][typeLowerCase] = 0;
        }

        for (const planId of plans) {
          const pageIndex = updatedGroupedPagesPlans.findIndex(
            (page) => page.pageId === pageNumber
          );

          if (updatedGroupedPagesPlans[pageIndex]?.groupedPlans !== undefined) {
            const planIndex = updatedGroupedPagesPlans[pageIndex]?.groupedPlans[
              typeLowerCase
            ].findIndex((plan) => plan.id === Number(planId));

            if (pageIndex !== -1) {
              if (planIndex !== -1) {
                const currentPageTypeOrder =
                  clickOrdersAux[pageNumber][typeLowerCase];

                const planId =
                  updatedGroupedPagesPlans[pageIndex]?.groupedPlans[
                    typeLowerCase
                  ][planIndex].id;

                pagePlanOrderTypeAux[pageNumber][typeLowerCase][planId] =
                  currentPageTypeOrder + 1;

                updatedGroupedPagesPlans[pageIndex].groupedPlans[typeLowerCase][
                  planIndex
                ].order = currentPageTypeOrder + 1;

                clickOrdersAux[pageNumber][typeLowerCase] =
                  currentPageTypeOrder + 1;
              }
            }
          }
        }
      }
    });

    const updatedGroupedPagesAux = updatedGroupedPagesPlans.map((page) => {
      if (selectedPagePlans.length === 0) {
        const updatedSpreadPlans = page.spreadPlans.map((plan) => {
          const { order, ...planAux } = plan;
          return planAux;
        });

        return {
          ...page,
          spreadPlans: updatedSpreadPlans,
          groupedPlans: updatedSpreadPlans,
        };
      }

      const updatedSpreadPlans = page.spreadPlans.map((plan) => {
        const selectedPage = selectedPagePlans.find(
          (selectedPage) => selectedPage.pageId === page.pageId
        );

        if (selectedPage) {
          const planExists = selectedPage.data.some((typeData) =>
            typeData.plans.includes(plan.id)
          );

          if (!planExists && plan.order !== undefined) {
            const { order, ...planAux } = plan;
            return planAux;
          }
        }

        return plan;
      });

      return {
        ...page,
        spreadPlans: updatedSpreadPlans,
        groupedPlans: updatedSpreadPlans,
      };
    });

    setGroupedPagesPlans(groupPlansByPage(updatedGroupedPagesAux));
    setPagePlanOrderType(pagePlanOrderTypeAux);
  }, [selectedPagePlans]);

  useEffect(() => {
    const pageContributionOrderTypeAux: PageTypeContributionOrder = {};
    const clickOrdersAux: ClickPageContributionTypeOrders = {};
    const updatedGroupedPagesPlans = [...groupedPagesPlans];

    selectedPageContributions.forEach((pageContribution) => {
      const pageNumber = pageContribution.pageId;
      const plans = pageContribution.data;
      if (!pageContributionOrderTypeAux[pageNumber]) {
        pageContributionOrderTypeAux[pageNumber] = {};
        clickOrdersAux[pageNumber] = 0;
      }

      for (const planId of plans) {
        const pageIndex = updatedGroupedPagesPlans.findIndex(
          (page) => page.pageId === pageNumber
        );

        if (
          updatedGroupedPagesPlans[pageIndex]?.groupedContributions !==
          undefined
        ) {
          const contributionIndex = updatedGroupedPagesPlans[
            pageIndex
          ]?.groupedContributions.findIndex(
            (plan) => plan.id === Number(planId)
          );

          if (pageIndex !== -1) {
            if (contributionIndex !== -1) {
              const currentPageTypeOrder = clickOrdersAux[pageNumber];

              const planId =
                updatedGroupedPagesPlans[pageIndex]?.groupedContributions[
                  contributionIndex
                ].id;

              if (planId === undefined) return;
              pageContributionOrderTypeAux[pageNumber][planId] =
                currentPageTypeOrder + 1;

              updatedGroupedPagesPlans[pageIndex].groupedContributions[
                contributionIndex
              ].order = currentPageTypeOrder + 1;

              clickOrdersAux[pageNumber] = currentPageTypeOrder + 1;
            }
          }
        }
      }
    });

    const updatedGroupedPagesAux = updatedGroupedPagesPlans.map((page) => {
      if (selectedPageContributions.length === 0) {
        const updatedSpreadPlans = page.contributionData.map((contribution) => {
          const { order, ...contributionAux } = contribution;
          return contributionAux;
        });

        return {
          ...page,
          contributionData: updatedSpreadPlans,
          groupedContributions: updatedSpreadPlans,
        };
      }
      const updatedSpreadPlans = page.contributionData.map((contribution) => {
        const selectedPage = selectedPageContributions.find(
          (selectedPage) => selectedPage.pageId === page.pageId
        );

        if (selectedPage) {
          const contributionExists = selectedPage.data.some(
            (contributionData) => contributionData === contribution.id
          );

          if (!contributionExists) {
            const { order, ...contributionAux } = contribution;
            return contributionAux;
          }
        }

        return contribution;
      });

      return {
        ...page,
        contributionData: updatedSpreadPlans,
        groupedContributions: updatedSpreadPlans,
      };
    });

    setGroupedPagesPlans(groupPlansByPage(updatedGroupedPagesAux));
    setPageContributionOrderType(pageContributionOrderTypeAux);
  }, [selectedPageContributions]);

  const mergePages = (
    ...pages: IGenerateAutobuildPage[]
  ): IGenerateAutobuildPage[] => {
    const mergedPages: Record<number, IGenerateAutobuildPage> = {};

    const mergePageData = (
      target: IGenerateAutobuildPage,
      source: IGenerateAutobuildPage
    ) => {
      target.spreadPlans = { ...target.spreadPlans, ...source.spreadPlans };
      target.contribution = { ...target.contribution, ...source.contribution };
      return target;
    };

    pages.forEach((page) => {
      const pageId = page.pageId;

      if (mergedPages[pageId]) {
        mergedPages[pageId] = mergePageData(mergedPages[pageId], page);
      } else {
        mergedPages[pageId] = { ...page };
      }
    });

    return Object.values(mergedPages);
  };

  const handleGenerateAutobuild = useCallback(() => {
    if (templateGroupId === undefined) {
      setError({ error: 'Please select a template group' });
      return;
    }

    if (
      selectedPageContributions.length === 0 &&
      selectedPagePlans.length === 0
    ) {
      setError({ error: 'Please select at least one plan or contribution' });
      return;
    }

    const autoBuildPlanPages = [];
    const autoBuildContributionPages = [];

    if (Object.entries(pagePlanOrderType).length > 0) {
      for (const page in pagePlanOrderType) {
        const pageIndex = groupedPagesPlans.findIndex(
          (groupedPage) => groupedPage.pageId === Number(page)
        );

        const plans = pagePlanOrderType[page];
        const contributions = pageContributionOrderType[page];
        const spreadPlans: AutobuildSpreadPlansOrder = {};

        for (const key in plans) {
          const planType = replaceTypeName(key);
          const plansAux = plans[key];
          const newPlans: Record<string, string> = {};

          for (const planId in plansAux) {
            newPlans[plansAux[planId].toString()] = planId;
          }

          spreadPlans[planType] = newPlans;
        }

        const newContribution: Record<number, number> = {};

        for (const id in contributions) {
          newContribution[Number(contributions[id])] = Number(id);
        }

        const autobuildPagesAux: IGenerateAutobuildPage = {
          pageId: Number(page),
          pageType: groupedPagesPlans[pageIndex].pageType,
          title: groupedPagesPlans[pageIndex].title,
          spreadPlans,
          contribution: newContribution,
        };

        autoBuildPlanPages.push(autobuildPagesAux);
      }
    }

    if (Object.entries(pageContributionOrderType).length > 0) {
      for (const page in pageContributionOrderType) {
        const pageIndex = groupedPagesPlans.findIndex(
          (groupedPage) => groupedPage.pageId === Number(page)
        );

        const contributions = pageContributionOrderType[page];
        const newContribution: Record<number, number> = {};

        for (const id in contributions) {
          newContribution[Number(contributions[id])] = Number(id);
        }

        const autobuildPagesAux: IGenerateAutobuildPage = {
          pageId: Number(page),
          pageType: groupedPagesPlans[pageIndex].pageType,
          title: groupedPagesPlans[pageIndex].title,
          spreadPlans: {},
          contribution: newContribution,
        };

        autoBuildContributionPages.push(autobuildPagesAux);
      }
    }

    const mergedPages = mergePages(
      ...autoBuildPlanPages,
      ...autoBuildContributionPages
    );

    const autoBuildJson = {
      quoteId: String(quotesDatesSelected?.id),
      courseTemplateId: templateGroupId,
      autoBuildPages: mergedPages,
    };

    setError({});
    onAccept(autoBuildJson);
  }, [
    selectedPageContributions,
    selectedPagePlans,
    pageContributionOrderType,
    groupedPagesPlans,
    templateGroupId,
    quotesDatesSelected?.id,
    onAccept,
    pagePlanOrderType,
  ]);

  const handleDeclineAutoBuild = useCallback(() => {
    if (!isSelectOpen.current.size) {
      onDecline?.();
    }
  }, [onDecline]);

  const handleTemplateGroupId = useCallback(
    (value: string) => {
      setGroupedPagesPlans([]);
      setSelectedPagePlans([]);
      setSelectedPageContributions([]);
      setTemplateGroupId(Number(value));
      getPlansByPage(Number(value));
    },
    [getPlansByPage]
  );

  const resetState = useCallback(() => {
    setError({});
    setPagePlanOrderType({});
    setPageContributionOrderType({});
    setGroupedPagesPlans([]);
    setSelectedPagePlans([]);
    setSelectedPageContributions([]);
    setTemplateGroupId(undefined);
  }, []);

  useEffect(() => {
    if (!open) resetState();
  }, [open, resetState]);

  return !open ? null : (
    <FormModalWide
      loading={isLoading}
      biggerModal
      dataCy="modal-autoBuild"
      {...props}
      open={open}
      acceptText="Generate"
      mainText={'Auto Build Course'}
      onAccept={handleGenerateAutobuild}
      onDecline={handleDeclineAutoBuild}
      onOpenChange={handleDeclineAutoBuild}
      zIndex={zIndex}
      disabled={
        selectedPageContributions.length === 0 && selectedPagePlans.length === 0
      }
    >
      <FormBase.Content>
        <DividerTitle>
          {quotesDatesSelected?.period} - {quotesDatesSelected?.effectiveDate}
        </DividerTitle>
        <DividerTitle>Build Template</DividerTitle>
        <CustomSelectData
          data-cy="Select a template group"
          data={templatesGroups}
          placeholder="Select a template group"
          onOpenChange={(isOpen: boolean) => {
            handleOpenChange(isOpen, 'templateGroup');
          }}
          name="templateGroup"
          onValueChange={handleTemplateGroupId}
          required
          zIndex={10}
        />

        {loadingGePagePlans ? (
          <ListLoading />
        ) : groupedPagesPlans !== undefined &&
          groupedPagesPlans.length === 0 ? (
          <TextContainer data-cy="text-container">
            <Text>No plans found</Text>
          </TextContainer>
        ) : groupedPagesPlans !== undefined ? (
          <CustomAccordion
            type="multiple"
            defaultValue={[
              `${
                groupedPagesPlans !== undefined && groupedPagesPlans.length > 0
                  ? String(groupedPagesPlans.at(0)?.pageId)
                  : 'item-01'
              }`,
            ]}
          >
            {groupedPagesPlans.map((page: IGroupedPages) => {
              const pageIndex = selectedPageContributions.findIndex(
                ({ pageId }) => pageId === page.pageId
              );

              const qtdContribution =
                pageIndex !== -1
                  ? selectedPageContributions[pageIndex] !== undefined
                    ? selectedPageContributions[pageIndex].data.length
                    : 0
                  : 0;

              return (
                (page.groupedPlans !== undefined ||
                  page.groupedContributions !== undefined) && (
                  <CustomAccordionItem
                    key={page.pageId}
                    value={`item-${page.pageId}`}
                  >
                    <CustomAccordionTrigger>
                      {page.title}
                    </CustomAccordionTrigger>
                    <CustomAccordionContent>
                      {Object.entries(page.groupedPlans).length > 0 && (
                        <>
                          <TextTitle>Plan Order</TextTitle>
                          <ListContainer>
                            {page.groupedPlans !== undefined &&
                              Object.entries(page.groupedPlans).map(
                                ([type, plans]) => {
                                  let qtd = 0;
                                  let typeIndex = 0;

                                  const pageIndex = selectedPagePlans.findIndex(
                                    (pagePlan) =>
                                      pagePlan.pageId === page.pageId
                                  );

                                  if (pageIndex !== -1) {
                                    const pagePlan =
                                      selectedPagePlans[pageIndex];
                                    typeIndex = pagePlan.data.findIndex(
                                      (typeData) =>
                                        replaceTypeName(typeData.type) ===
                                        replaceTypeName(type)
                                    );

                                    if (typeIndex !== -1) {
                                      qtd = pagePlan.data[typeIndex]?.qtd || 0;
                                    }
                                  }

                                  return (
                                    <Container key={type}>
                                      <SpacedContainer>
                                        <PlanGroupName>
                                          {type
                                            .toLowerCase()
                                            .includes('critical')
                                            ? 'Critical Illness'
                                            : type}
                                        </PlanGroupName>
                                        <PlanGroupName>
                                          ({qtd}/
                                          {
                                            page.pageType[
                                              type as keyof IPageType
                                            ]
                                          }
                                          )
                                        </PlanGroupName>
                                      </SpacedContainer>
                                      <TableContainer>
                                        <CustomTable>
                                          <Thead type="Carrier" />
                                          <Tbody>
                                            {plans.map((plan) => {
                                              const isDisabled =
                                                Number(qtd) >=
                                                  Number(
                                                    page.pageType[
                                                      type as keyof IPageType
                                                    ]
                                                  ) &&
                                                !selectedPagePlans[
                                                  pageIndex
                                                ].data[
                                                  typeIndex
                                                ].plans.includes(plan.id);

                                              return (
                                                <TrSpreadPlan key={plan.id}>
                                                  <Td textAlign="center">
                                                    <CheckContainer data-cy="plan-checkbox-container">
                                                      <CustomBaseCheckbox
                                                        id={`${
                                                          page.pageId
                                                        } ${plan.id.toString()}`}
                                                        checked={selectedPagePlans.some(
                                                          (pageAux) =>
                                                            pageAux.pageId ===
                                                              page.pageId &&
                                                            pageAux.data.some(
                                                              (typeData) =>
                                                                replaceTypeName(
                                                                  typeData.type
                                                                ) ===
                                                                  replaceTypeName(
                                                                    type
                                                                  ) &&
                                                                typeData.plans.includes(
                                                                  plan.id
                                                                )
                                                            )
                                                        )}
                                                        disabled={isDisabled}
                                                        dataCy={'plan-checkbox'}
                                                        name="change-plan"
                                                        onClick={() => {
                                                          togglePagePlanSelection(
                                                            plan.id,
                                                            page.pageId,
                                                            plan.type
                                                          );
                                                        }}
                                                      />
                                                    </CheckContainer>
                                                  </Td>
                                                  <Td
                                                    textAlign="left"
                                                    width="420px"
                                                  >
                                                    <LabelCheckBox
                                                      data-cy="label-planName"
                                                      htmlFor={`${
                                                        page.pageId
                                                      } ${plan.id.toString()}`}
                                                      disabled={isDisabled}
                                                    >
                                                      {plan.name}
                                                      {plan.medical?.network
                                                        ? ' - ' +
                                                          plan.medical?.network
                                                        : ''}{' '}
                                                      {plan.dental?.network
                                                        ? ' - ' +
                                                          plan.dental?.network
                                                        : ''}{' '}
                                                      {plan.vision?.network
                                                        ? ' - ' +
                                                          plan.vision?.network
                                                        : ''}{' '}
                                                    </LabelCheckBox>
                                                  </Td>
                                                  <Td
                                                    textAlign="left"
                                                    width="110px"
                                                  >
                                                    <LabelCheckBox
                                                      data-cy="label-carrier"
                                                      htmlFor={`${
                                                        page.pageId
                                                      } ${plan.id.toString()}`}
                                                      disabled={isDisabled}
                                                    >
                                                      {plan.carrier.name}
                                                    </LabelCheckBox>
                                                  </Td>
                                                  <TdArrows>
                                                    <LabelCheckBox>
                                                      {plan.order !==
                                                        undefined && (
                                                        <ButtonArrows
                                                          onArrowClick={
                                                            handleArrowClick
                                                          }
                                                          id={plan.id}
                                                          prop={type}
                                                          type="plan"
                                                          pageId={page.pageId}
                                                          text={`Plan ${String(
                                                            plan.order
                                                          )}`}
                                                        />
                                                      )}
                                                    </LabelCheckBox>
                                                  </TdArrows>
                                                </TrSpreadPlan>
                                              );
                                            })}
                                          </Tbody>
                                        </CustomTable>
                                      </TableContainer>
                                    </Container>
                                  );
                                }
                              )}
                          </ListContainer>
                        </>
                      )}
                      {Object.entries(page.groupedContributions).length > 0 &&
                        page.groupedContributions.length > 0 && (
                          <>
                            <SpacedContainer>
                              <TextTitle>Contribution Order</TextTitle>
                              <TextTitle fontWeight={'normal'}>
                                (
                                {String(qtdContribution) +
                                  '/' +
                                  String(page.pageType.contribution)}
                                )
                              </TextTitle>
                            </SpacedContainer>
                            <ListContainer>
                              <TableContainer>
                                <CustomTable>
                                  <Thead type="Type" />
                                  <Tbody>
                                    {Object.values(
                                      page.groupedContributions
                                    ).flatMap((contribution) => {
                                      const isDisabled =
                                        qtdContribution >=
                                          page.pageType.contribution &&
                                        !selectedPageContributions.some(
                                          (pageAux) =>
                                            pageAux.pageId === page.pageId &&
                                            pageAux.data.includes(
                                              contribution.id
                                            )
                                        );
                                      return (
                                        contribution.id !== undefined && (
                                          <Tr key={contribution.id}>
                                            <Td textAlign="center">
                                              <CheckContainer data-cy="contribution-checkbox-container">
                                                <CustomBaseCheckbox
                                                  id={`${
                                                    page.pageId
                                                  } ${contribution.id.toString()}`}
                                                  checked={selectedPageContributions.some(
                                                    (pageAux) =>
                                                      pageAux.pageId ===
                                                        page.pageId &&
                                                      pageAux.data.some(
                                                        (contributionData) =>
                                                          contributionData ===
                                                          contribution.id
                                                      )
                                                  )}
                                                  dataCy={
                                                    'contribution-checkbox'
                                                  }
                                                  name="change-contribution"
                                                  onClick={() => {
                                                    togglePageContributionSelection(
                                                      contribution.id,
                                                      page.pageId
                                                    );
                                                  }}
                                                  disabled={isDisabled}
                                                />
                                              </CheckContainer>
                                            </Td>
                                            <Td textAlign="left" width="420px">
                                              <LabelCheckContainer data-cy="container-contributionName-checkbox">
                                                <LabelCheckBox
                                                  htmlFor={`${
                                                    page.pageId
                                                  } ${contribution.id.toString()}`}
                                                  fontWeight="500"
                                                  disabled={isDisabled}
                                                >
                                                  {
                                                    contribution
                                                      .contributionClass.title
                                                  }
                                                </LabelCheckBox>
                                                <LabelCheckBox
                                                  htmlFor={`${
                                                    page.pageId
                                                  } ${contribution.id.toString()}`}
                                                  fontWeight="300"
                                                  disabled={isDisabled}
                                                >
                                                  {contribution.spreadPlan.name}
                                                </LabelCheckBox>
                                              </LabelCheckContainer>
                                            </Td>
                                            <Td textAlign="left">
                                              <LabelCheckBox
                                                data-cy="label-type"
                                                htmlFor={`${
                                                  page.pageId
                                                } ${contribution.id.toString()}`}
                                                disabled={isDisabled}
                                              >
                                                {contribution.spreadPlan.type}
                                              </LabelCheckBox>
                                            </Td>
                                            <TdArrows>
                                              <LabelCheckBox>
                                                {contribution.order !==
                                                  undefined && (
                                                  <ButtonArrows
                                                    onArrowClick={
                                                      handleArrowClick
                                                    }
                                                    id={contribution.id}
                                                    prop={'contribution'}
                                                    type="contribution"
                                                    pageId={page.pageId}
                                                    text={`Plan ${String(
                                                      contribution.order
                                                    )}`}
                                                  />
                                                )}
                                              </LabelCheckBox>
                                            </TdArrows>
                                          </Tr>
                                        )
                                      );
                                    })}
                                  </Tbody>
                                </CustomTable>
                              </TableContainer>
                            </ListContainer>
                          </>
                        )}
                    </CustomAccordionContent>
                  </CustomAccordionItem>
                )
              );
            })}
          </CustomAccordion>
        ) : null}
        {error.error && <ErrorMessage>{error.error}</ErrorMessage>}
      </FormBase.Content>
    </FormModalWide>
  );
};

const Thead = ({ type }: { type: string }): JSX.Element => {
  return (
    <thead>
      <TrSpreadPlan>
        <Th textAlign="center">Include</Th>
        <Th textAlign="left">Plan</Th>
        <Th textAlign="left">{type}</Th>
        <Th textAlign="right">
          Plan Order
          <CustomBaseTooltip message="Set the order of where each plan will be in the course tables. To change, click the up or down arrow to switch the order.">
            <span>
              <InfoIcon size={12} />
            </span>
          </CustomBaseTooltip>
        </Th>
      </TrSpreadPlan>
    </thead>
  );
};
